import LocationCombobox from '@/components/General/LocationCombobox';
import { Kpi } from '@/domain/Kpi/Kpi';
import useDebounce from '@/hooks/useDebounce';
import { Button, DataGrid, Icon, Search, Toggle } from '@hydrodigital/design-system';
import {
	Combobox,
	ComboboxInput,
	ComboboxContent,
	ComboboxList,
	ComboboxItem,
	ComboboxEmpty,
} from '@hydrodigital/combobox';
import type { ComboboxOption } from '@hydrodigital/combobox';
import { Tooltip, notification } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import CreateKpiModal from '../../components/Kpi/CreateKpiModal';
import ConfirmationModal from '../../components/Modal/ConfirmationModal';
import KpiService from '../../domain/Kpi/KpiService';
import UserService from '../../domain/Users/UserService';

function useQuery() {
	return new URLSearchParams(useLocation().search);
}

const kpiService = new KpiService();
const userService = new UserService();

const inUseOptions: ComboboxOption[] = [
	{ value: 'true', label: 'Active' },
	{ value: 'false', label: 'Inactive' },
];

export default function KpiView() {
	const query = useQuery();
	const [name, setName] = useState<string>('');
	const [paginationState, setPaginationState] = useState({
		pageIndex: 0,
		pageSize: 10,
	});
	const [totalRecords, setTotalRecords] = useState(0);
	const debouncedName = useDebounce(name, 500);
	const [showModalCreation, setShowModalCreation] = useState(false);
	const [selectKpi, setSelectKpi] = useState<any>(null);
	const [kpiList, setKpiList] = useState<Kpi[]>([]);
	const [selectLocation, setSelectLocation] = useState<string | null>(null);
	const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
	const [users, setUsers] = useState<any[]>([]);
	const [inUseQuery, setinUseQuery] = useState<boolean | null>(null);
	const [showConfirmDelete, setshowConfirmDelete] = useState(false);

	const columns = [
		{
			accessorKey: 'name',
			header: 'KPI Name',
			cell: (cellContext: any) => cellContext.getValue(),
		},
		{
			accessorKey: 'location',
			header: 'Location',
			cell: (cellContext: any) => {
				const row = cellContext.row.original as Kpi;
				return (
					<div className="flex column">
						<span>{row.location}</span>
						<span style={{ color: '#74788D', fontSize: '10px' }}>{row.country}</span>
					</div>
				);
			},
		},
		{
			accessorKey: 'infrastructure',
			header: 'Asset',
			cell: (cellContext: any) => cellContext.getValue(),
		},
		{
			accessorKey: 'parameter',
			header: 'Parameter',
			cell: (cellContext: any) => cellContext.getValue(),
		},
		{
			accessorKey: 'createdBy',
			header: 'Created by',
			cell: (cellContext: any) => {
				const row = cellContext.row.original as Kpi;
				return (
					<div className="flex column">
						<span>{row.createdBy}</span>
						<span style={{ color: '#74788D', fontSize: '10px' }}>
							{dayjs(row.createdAt).format('YYYY/MM/DD [at] hh:mm a')}
						</span>
					</div>
				);
			},
		},
		{
			accessorKey: 'enabled',
			header: 'In Use',
			cell: (cellContext: any) => {
				const row = cellContext.row.original as Kpi;
				return (
					<div className="flex">
						<Toggle
							key={`toggle-${row.id}-${row.enabled}`}
							defaultChecked={row.enabled}
							checkedText="ON"
							uncheckedText="OFF"
							size="small"
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								handleChangeEnabledKpi(row.id, e.target.checked);
							}}
						/>
					</div>
				);
			},
		},
		{
			accessorKey: 'actions',
			header: 'Actions',
			cell: (cellContext: any) => {
				const row = cellContext.row.original as Kpi;
				return (
					<div className="flex" style={{ gap: '12px' }}>
						<Tooltip title="Edit">
							<span
								style={{ cursor: 'pointer', display: 'inline-flex' }}
								onClick={() => {
									setSelectKpi(row.id);
									setShowModalCreation(true);
								}}
							>
								<Icon name="edit-alt" size="m" hoverable />
							</span>
						</Tooltip>
						<Tooltip title="Delete">
							<span
								style={{ cursor: 'pointer', display: 'inline-flex' }}
								onClick={() => {
									setSelectKpi(row);
									setshowConfirmDelete(true);
								}}
							>
								<Icon name="trash-alt" size="m" hoverable />
							</span>
						</Tooltip>
					</div>
				);
			},
		},
	];

	async function handleDeleteKpi(kpiId: number) {
		kpiService.deleteKpi(kpiId).then(() => {
			notification.success({
				message: 'Successful',
				description: 'Kpi deleted successfully',
			});
			setshowConfirmDelete(false);
			handleGetKpi();
		});
	}

	const handleGetKpi = useCallback(async () => {
		const oui = JSON.parse(localStorage.getItem('security') || '{}').organizationId;
		const response = await kpiService.getKpiByOrganization(
			oui,
			paginationState.pageIndex + 1,
			paginationState.pageSize,
			debouncedName || null,
			inUseQuery,
			selectedUserId,
			selectLocation,
		);
		if (response) {
			setKpiList(response.data);
			setTotalRecords(response.totalRecords);
		}
	}, [paginationState, debouncedName, inUseQuery, selectedUserId, selectLocation]);

	async function handleChangeEnabledKpi(kpiId: string | number, enabled: boolean) {
		const response = await kpiService.changeEnableKpi(kpiId, enabled);
		// Actualizar el estado local para reflejar el cambio en el DataGrid
		setKpiList((prevList) =>
			prevList.map((kpi) => (kpi.id === kpiId ? { ...kpi, enabled } : kpi)),
		);
	}

	const handleGetUsers = () => {
		const oui = JSON.parse(localStorage.getItem('security') || '{}').organizationId;
		const res = userService.getAllUsers(oui, 1, 1000, null, null);

		res
			.then((response) => {
				setUsers(response.responseContentDTO);
			})
			.catch((error) => {
				console.error(error);
			});
	};

	useEffect(() => {
		handleGetUsers();
		if (query.get('kpiId')) {
			setSelectKpi(query.get('kpiId'));
			setShowModalCreation(true);
		}
	}, []);

	useEffect(() => {
		handleGetKpi();
	}, [handleGetKpi]);

	useEffect(() => {
		setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
	}, [selectLocation, selectedUserId, inUseQuery, debouncedName]);

	return (
		<>
			{kpiList.length > 0 ||
			debouncedName.length > 0 ||
			selectedUserId ||
			selectLocation ||
			inUseQuery != null ? (
				<div className="px-4">
					<div className="text-title-page mb-4"> KPIs </div>
					<div className="flex justify-between mb-3">
						<div className="flex">
							<div className="mr-2">
								<Search
									placeholder="Search"
									size="small"
									value={name}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
										setName(e.target.value);
									}}
									onClear={() => {
										setName('');
									}}
									showClearButton
								/>
							</div>
							<div style={{ width: '200px' }} className="mr-2">
								<LocationCombobox
									value={selectLocation}
									onValueChange={setSelectLocation}
								/>
							</div>
							<div style={{ width: '200px' }} className="mr-2">
								<Combobox
									items={(users || []).map((u: any) => ({
										value: String(u.userId),
										label: `${u.firstName} ${u.lastName}`,
									}))}
									value={selectedUserId ? String(selectedUserId) : undefined}
									onValueChange={(val) => setSelectedUserId(val)}
									onClear={() => setSelectedUserId(null)}
									size="small"
								>
									<ComboboxInput placeholder="User" />
									<ComboboxContent>
										<ComboboxEmpty>No users found.</ComboboxEmpty>
										<ComboboxList>
											{(item) => (
												<ComboboxItem key={item.value} value={item}>
													{item.label}
												</ComboboxItem>
											)}
										</ComboboxList>
									</ComboboxContent>
								</Combobox>
							</div>
							<div style={{ width: '200px' }}>
								<Combobox
									items={inUseOptions}
									value={inUseQuery !== null && inUseQuery !== undefined ? String(inUseQuery) : undefined}
									onValueChange={(val) => setinUseQuery(val === 'true')}
									onClear={() => setinUseQuery(null)}
									searchable={false}
									size="small"
								>
									<ComboboxInput placeholder="In Use" />
									<ComboboxContent>
										<ComboboxEmpty>No options.</ComboboxEmpty>
										<ComboboxList>
											{(item) => (
												<ComboboxItem key={item.value} value={item}>
													{item.label}
												</ComboboxItem>
											)}
										</ComboboxList>
									</ComboboxContent>
								</Combobox>
							</div>
						</div>
						<Button
							iconName="plus-circle"
							size="small"
							variant="secondary"
							onClick={() => {
								setShowModalCreation(true);
							}}
							label="Add Kpi"
						/>
					</div>
					<DataGrid
						columns={columns}
						data={kpiList || []}
						pagination={{
							enabled: true,
							manualPagination: true,
							rowCount: totalRecords,
							defaultPageSize: 10,
							pageSizeOptions: [10, 20, 50],
						}}
						stickyHeader={true}
						maxHeight="calc(100vh - 280px)"
						paginationState={paginationState}
						onPaginationChange={setPaginationState}
					/>
				</div>
			) : (
				<div
					className="flex flex-column self-center justify-center text-center"
					style={{ width: '100%', marginTop: '25%' }}
				>
					<span className="text-title-page" style={{ marginBottom: '10px' }}>
						No KPIs yet!
					</span>
					<span className="text-subtitle-page" style={{ marginBottom: '10px' }}>
						Looks that you haven’t create any KPI, no worries. Click to start
					</span>
					<div className="self-center">
						<Button
							iconName="plus-circle"
							label="Create KPI"
							onClick={() => {
								setShowModalCreation(true);
							}}
						/>
					</div>
				</div>
			)}
			{showModalCreation && (
				<CreateKpiModal
					isModalOpen={showModalCreation}
					onSuccess={() => {
						setShowModalCreation(false);
						handleGetKpi();
					}}
					onCancel={() => {
						setSelectKpi(null);
						setShowModalCreation(false);
					}}
					selectedKpi={selectKpi}
				/>
			)}
			<ConfirmationModal
				title="Delete KPI?"
				message="Are you sure to delete it?"
				showModal={showConfirmDelete}
				onAccept={() => {
					if (selectKpi) {
						handleDeleteKpi(selectKpi.id);
					}
				}}
				oncancel={() => {
					setshowConfirmDelete(false);
				}}
			/>
		</>
	);
}
